/**
 * In-article product CTAs for buying-guide articles.
 *
 * Responsibilities:
 *   1. Keep the paid landing-page test marker (wr_lp_test) on every internal
 *      shop link the reader can click out of the guide, so the paid segment
 *      stays measurable through to the product page.
 *   2. Reveal the mobile sticky CTA bar once the reader is past the first card.
 *   3. Emit GA4 view/click events per CTA placement.
 *
 * The CTAs are plain links to the product page. Nothing here posts to the cart
 * or duplicates an existing add_to_cart event.
 */
(function () {
  'use strict';

  if (window.__wrGuideCtaInit) return;
  window.__wrGuideCtaInit = true;

  var MARKER_PARAM = 'wr_lp_test';
  var LINK_SELECTOR = 'a[href^="/products/"], a[href^="/collections/"]';

  /**
   * gtag first: the store runs gtag.js with no GTM container, so a bare
   * dataLayer.push is never turned into a GA4 event. Mirrors the order used
   * by snippets/review-tracking.liquid, whose events do reach GA4.
   */
  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
      }
    } catch {
      /* Analytics must never break a CTA. */
    }
  }

  function placementOf(element) {
    var host = element.closest('[data-wr-cta-placement]');
    return (host && host.getAttribute('data-wr-cta-placement')) || 'in-content';
  }

  /* ------------------------------------------------- paid marker carry-over */

  function markerValue() {
    try {
      return new window.URLSearchParams(window.location.search).get(MARKER_PARAM) || '';
    } catch {
      return '';
    }
  }

  function carryMarker(marker, scope) {
    if (!marker) return;

    var links = scope.querySelectorAll(LINK_SELECTOR);

    Array.prototype.forEach.call(links, function (link) {
      try {
        var url = new window.URL(link.getAttribute('href'), window.location.origin);
        if (url.searchParams.has(MARKER_PARAM)) return;
        url.searchParams.set(MARKER_PARAM, marker);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      } catch {
        /* Leave any link we cannot parse exactly as authored. */
      }
    });
  }

  /* ------------------------------------------------------------- reporting */

  function observeViews(cards) {
    if (!window.IntersectionObserver) return;

    var observer = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          track('guide_cta_view', {
            cta_placement: entry.target.getAttribute('data-wr-cta-placement') || 'in-content'
          });
        });
      },
      { threshold: 0.5 }
    );

    Array.prototype.forEach.call(cards, function (card) {
      observer.observe(card);
    });
  }

  /* ----------------------------------------------------------- sticky bar */

  function initSticky(sticky, firstCard) {
    if (!sticky || !firstCard || !window.IntersectionObserver) return;

    var observer = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Show once the first card has scrolled off the top of the viewport.
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.hidden = !passed;
        });
      },
      { threshold: 0 }
    );

    observer.observe(firstCard);
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    var content = document.querySelector('.article-template__content');
    var cards = document.querySelectorAll('.wr-guide-cta[data-wr-cta-placement]');
    if (!cards.length) return;

    var sticky = document.querySelector('[data-wr-guide-sticky]');
    var marker = markerValue();

    // The bar is authored inside the article body. Re-parent it so a
    // transformed ancestor (theme scroll animations) can never turn its
    // position: fixed into position: absolute.
    if (sticky && sticky.parentElement !== document.body) {
      document.body.appendChild(sticky);
    }

    if (content) carryMarker(marker, content);
    if (sticky) carryMarker(marker, sticky);

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-wr-cta-button]');
      if (!button) return;

      var params = {
        cta_placement: placementOf(button),
        cta_destination: button.getAttribute('href') || ''
      };
      var href = button.href;
      var sameTab =
        href &&
        event.button === 0 &&
        !event.defaultPrevented &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        !event.altKey &&
        (!button.target || button.target === '_self');

      // Modifier/middle clicks and new-tab links keep this page open, and
      // without gtag there is no callback to wait for: send and move on.
      if (!sameTab || typeof window.gtag !== 'function') {
        track('guide_cta_click', params);
        return;
      }

      // A plain click unloads the page before the hit leaves. Hold navigation
      // until gtag confirms the send, capped by a timer in case the Google tag
      // is still a queue stub (it lazy-loads on first interaction).
      event.preventDefault();

      var navigated = false;
      function go() {
        if (navigated) return;
        navigated = true;
        window.location.assign(href);
      }

      window.setTimeout(go, 350);

      try {
        window.gtag(
          'event',
          'guide_cta_click',
          Object.assign({}, params, { event_callback: go, event_timeout: 300 })
        );
      } catch {
        go();
      }
    });

    observeViews(cards);
    initSticky(sticky, cards[0]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
